// =============================================================================
// Copy to clipboard for Unlayer Button and linked Paragraph/Heading text.
//
// Unlayer has no built-in Copy action. We add one, then fix two Unlayer gaps:
//   1. {{copyText}} is shared — the last typed value can land on every Copy link.
//   2. Typing in Text to copy re-renders the field, so the caret jumps.
// Empty Text to copy is allowed: Save is not blocked, and a live click copies nothing.
//
// How the file is split:
//   PART 1 — the Action dropdown fields (Text to copy, Confirmation, Close popup)
//   PART 2 — on save/preview, write the real values onto each button/word
//   PART 3 — scripts we inject into the Unlayer editor (font + typing)
//
// Live store pages read: data-mm-click-copy, data-mm-copy-text, data-mm-confirm,
// data-mm-close-popup. Coupon links (.mm-coupon-copy) are never changed here.
// =============================================================================

const DEFAULT_COPY_CONFIRMATION = 'Copied!'
const COPY_LINK_NAME = 'copy_to_clipboard'

// -----------------------------------------------------------------------------
// PART 1 — What the user sees in Action Type = Copy to clipboard
// -----------------------------------------------------------------------------

// Adds "Copy to clipboard" to the Unlayer Action dropdown.
// showClose: popups get Yes/No "Close popup after copy". Landing pages do not.
export function getCopyToClipboardLinkType({ showClose = false } = {}) {
  const fields = [
    {
      name: 'copyText',
      label: 'Text to copy',
      defaultValue: '',
      inputType: 'text',
      placeholderText: 'e.g. FREEDELIVERY'
    },
    {
      name: 'confirmText',
      label: 'Confirmation text',
      defaultValue: DEFAULT_COPY_CONFIRMATION,
      inputType: 'text',
      placeholderText: DEFAULT_COPY_CONFIRMATION
    }
  ]

  // Native Unlayer dropdown (No = 0, Yes = 1). Only for popups, not landing pages.
  if (showClose) {
    fields.push({
      name: 'closePopup',
      label: 'Close popup after copy',
      defaultValue: '0',
      options: [
        { value: '0', label: 'No' },
        { value: '1', label: 'Yes' }
      ]
    })
  }

  return {
    name: COPY_LINK_NAME,
    label: 'Copy to clipboard',
    // {{copyText}} is Unlayer's template. On export it can copy the LAST value
    // onto every link — PART 2 overwrites these with the real per-block values.
    attrs: {
      // '#' not javascript:void(0): the editor strips javascript: hrefs on save, and an <a>
      // without href is not recognised as a link when the design is reopened.
      href: '#',
      target: '_self',
      'data-mm-click-copy': '1',
      'data-mm-copy-text': '{{copyText}}',
      'data-mm-confirm': '{{confirmText}}',
      'data-mm-close-popup': showClose ? '{{closePopup}}' : '0'
    },
    fields
  }
}

// -----------------------------------------------------------------------------
// PART 2 — Save / preview: give each button and each linked word its own text
//
// Unlayer design JSON looks like: body.rows → columns → contents (button, text…).
// Each content has an id like u_content_button_1. We walk that tree, read that
// block's Copy settings, and write them onto its <a> tags.
// -----------------------------------------------------------------------------

// Live click code only closes when the value is 1 / true / on / yes.
function closePopupAttr(value) {
  return /^(1|true|on|yes)$/i.test(String(value == null ? '' : value)) ? '1' : '0'
}

function isCopyLinkName(name) {
  return name === COPY_LINK_NAME
}

// Ignore Unlayer's leftover "{{copyText}}". Empty string means "copy nothing".
function usableCopyText(value) {
  if (value == null) return ''
  const text = String(value)
  if (!text || text === '{{copyText}}') return ''
  return text
}

// Unlayer may store the same fields in two places (values + attrs). Flatten them.
function normalizeCopyValues(values = {}) {
  const nested = values.values && typeof values.values === 'object' ? values.values : {}
  const merged = { ...values, ...nested }
  const copyText = usableCopyText(merged.copyText) || usableCopyText(merged['data-mm-copy-text'])
  let confirmText = merged.confirmText != null ? String(merged.confirmText) : (merged['data-mm-confirm'] != null ? String(merged['data-mm-confirm']) : DEFAULT_COPY_CONFIRMATION)
  if (confirmText === '{{confirmText}}') confirmText = DEFAULT_COPY_CONFIRMATION
  return {
    copyText,
    confirmText,
    closePopup: merged.closePopup != null ? merged.closePopup : merged['data-mm-close-popup']
  }
}

// True only when the Action dropdown is Copy to clipboard.
// Do not use data-mm-click-copy on href.attrs: Unlayer copies custom link
// attrs onto every button, including Open Website / Close / Go to Step.
function copyValuesFromHref(href) {
  if (!href || typeof href !== 'object') return null
  const named = isCopyLinkName(href.name) || isCopyLinkName(href.values && href.values.name)
  if (!named) return null
  return normalizeCopyValues(href)
}

// Our Copy <a> has href '#' (older saves: javascript:void(0) or no href). Open Website uses http(s).
function isCopyHtmlAnchor(el) {
  if (!el || (el.classList && el.classList.contains('mm-coupon-copy'))) return false
  if (String(el.getAttribute('data-mm-click-copy')) !== '1') return false
  const href = String(el.getAttribute('href') || '').replace(/\s/g, '')
  return href === '' || href === '#' || /^javascript:void\(0\);?$/i.test(href)
}

// Unlayer stores each link's own settings as base64 JSON in data-u-link-value.
function copyTextFromLinkValue(el) {
  try {
    const raw = el.getAttribute('data-u-link-value')
    if (!raw || typeof atob === 'undefined') return null
    const parsed = JSON.parse(decodeURIComponent(escape(atob(raw))))
    return usableCopyText(parsed && parsed.values && parsed.values.copyText)
  } catch (err) {
    return null
  }
}

// LINKED WORD: Copy settings live on <a> tags inside the paragraph HTML, not on href.
function copyValuesFromHtml(html) {
  if (typeof html !== 'string' || html.indexOf('data-mm-click-copy') === -1 || typeof DOMParser === 'undefined') return []
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return Array.from(doc.querySelectorAll('a[data-mm-click-copy="1"]:not(.mm-coupon-copy)')).filter(isCopyHtmlAnchor).map((el) => ({
    // Each <a> keeps its own typed value in data-u-link-value; the data-mm attr can hold another link's text.
    copyText: copyTextFromLinkValue(el) || usableCopyText(el.getAttribute('data-mm-copy-text')),
    confirmText: el.getAttribute('data-mm-confirm') === '{{confirmText}}' ? DEFAULT_COPY_CONFIRMATION : (el.getAttribute('data-mm-confirm') || DEFAULT_COPY_CONFIRMATION),
    closePopup: el.getAttribute('data-mm-close-popup')
  }))
}

// Visit every block in the same order as the page HTML (including nested rows).
function visitDesignContents(rows, onContent) {
  (rows || []).forEach((row) => {
    (row.columns || []).forEach((col) => {
      (col.contents || []).forEach((content) => {
        onContent(content)
        if (content && content.rows) visitDesignContents(content.rows, onContent)
      })
    })
  })
}

// Unlayer's id for this block, e.g. u_content_button_1.
function contentHtmlId(content) {
  return (content && content.values && content.values._meta && content.values._meta.htmlID) || ''
}

// Values we saved while the user was typing, keyed by block id.
// Example: { u_content_button_1: { copyText: 'SAVE10', confirmText: 'Copied!', closePopup: '1' } }
function copySidecar() {
  try {
    if (typeof window === 'undefined') return {}
    if (window.__mmCopyByHtmlId) return window.__mmCopyByHtmlId
    if (window.top && window.top.__mmCopyByHtmlId) return window.top.__mmCopyByHtmlId
  } catch (err) {}
  return {}
}

// Prefer the first object's confirm / close. Skip Unlayer placeholders like {{confirmText}}.
function mergeCopyMeta(primary, fallback) {
  const a = primary || {}
  const b = fallback || {}
  return {
    confirmText: (a.confirmText && a.confirmText !== '{{confirmText}}') ? a.confirmText : (b.confirmText || DEFAULT_COPY_CONFIRMATION),
    closePopup: a.closePopup != null && String(a.closePopup) !== '{{closePopup}}' ? a.closePopup : b.closePopup
  }
}

// True if this block has its own Text to copy in the sidecar (empty still counts — copy nothing).
function hasOwnCopyText(values) {
  return !!(values && Object.prototype.hasOwnProperty.call(values, 'copyText') && values.copyText !== '{{copyText}}')
}

// For ONE block, decide which Text to copy / confirm / close to use.
// Sidecar only fills typed values for a block that is ALREADY a Copy action.
// It must not turn a normal Open Website button into a Copy link.
function copyLinksForContent(content) {
  if (!content || !content.values) return []
  const values = content.values
  const fromHref = copyValuesFromHref(values.href) || copyValuesFromHref(values.action) || copyValuesFromHref(values.link)
  const fromHtml = copyValuesFromHtml(values.text).concat(copyValuesFromHtml(values.html))
  const side = copySidecar()[contentHtmlId(content)]
  const type = content.type || ''
  const meta = mergeCopyMeta(side, mergeCopyMeta(fromHref, fromHtml[0]))
  const sideText = hasOwnCopyText(side) ? usableCopyText(side.copyText) : null

  function withSide(link) {
    if (!link) return link
    return {
      ...link,
      ...meta,
      copyText: sideText != null ? sideText : usableCopyText(link.copyText)
    }
  }

  // A button has one Copy action, stored on href.name.
  if (type === 'button') {
    return fromHref ? [withSide(fromHref)] : []
  }

  // One linked word in a paragraph.
  if (fromHtml.length === 1) {
    const item = fromHref
      ? { copyText: usableCopyText(fromHref.copyText), ...mergeCopyMeta(fromHref, fromHtml[0]) }
      : fromHtml[0]
    return [withSide(item)]
  }

  // Several linked words in the same paragraph — keep each word's own text.
  // Do not apply the sidecar here: it holds only the last typed value for the whole block.
  if (fromHtml.length > 1) {
    return fromHtml.map((item) => ({
      ...meta,
      ...mergeCopyMeta(item, meta),
      copyText: usableCopyText(item.copyText)
    }))
  }

  return fromHref ? [withSide(fromHref)] : []
}

// All Copy links in page order. Used only if we cannot match by block id.
export function collectCopyClipboardLinks(design) {
  const links = []
  visitDesignContents(design && design.body && design.body.rows, (content) => {
    copyLinksForContent(content).forEach((link) => links.push(link))
  })
  return links
}

// Put the three live attributes on one <a>. Store JS reads these on click.
function stampCopyAttrs(el, link) {
  if (!el || !link) return
  el.setAttribute('data-mm-click-copy', '1')
  el.setAttribute('data-mm-copy-text', link.copyText || '')
  el.setAttribute('data-mm-confirm', link.confirmText || DEFAULT_COPY_CONFIRMATION)
  el.setAttribute('data-mm-close-popup', closePopupAttr(link.closePopup))
}

// Find Copy <a> tags in this block only. Skip coupon copy links.
function copyAnchorsInRoot(root) {
  if (!root) return []
  if (isCopyHtmlAnchor(root)) return [root]
  return Array.from(root.querySelectorAll('a[data-mm-click-copy="1"]:not(.mm-coupon-copy)')).filter(isCopyHtmlAnchor)
}

// Same stamp, but the HTML is a string inside design JSON (paragraph text/html).
function stampHtmlFragment(html, links) {
  if (!html || !links || !links.length || typeof DOMParser === 'undefined') return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  copyAnchorsInRoot(doc.body).forEach((el, index) => stampCopyAttrs(el, links[index] || links[0]))
  return doc.body ? doc.body.innerHTML : html
}

// Clone the design and write real copy values onto each block before we save it.
function stampCopyClipboardOnDesign(design) {
  if (!design) return design
  const cloned = JSON.parse(JSON.stringify(design))
  visitDesignContents(cloned.body && cloned.body.rows, (content) => {
    const links = copyLinksForContent(content)
    if (!links.length || !content.values) return
    const fromHref = copyValuesFromHref(content.values.href) || copyValuesFromHref(content.values.action) || copyValuesFromHref(content.values.link)
    const href = content.values.href || content.values.action || content.values.link
    // Only touch href when Action is Copy. Keep the editor's own href.attrs ({{copyText}} templates):
    // overwriting them with literal values stops the Text to copy fields redrawing on reload.
    // The live attrs are written onto the exported HTML by applyCopyClipboardAttrs.
    if (fromHref && href && typeof href === 'object') {
      href.values = href.values || {}
      if (links[0].copyText) href.values.copyText = links[0].copyText
      if (links[0].confirmText) href.values.confirmText = links[0].confirmText
    }
    if (typeof content.values.text === 'string') content.values.text = stampHtmlFragment(content.values.text, links)
    if (typeof content.values.html === 'string') content.values.html = stampHtmlFragment(content.values.html, links)
  })
  return cloned
}

// Stamp the exported HTML: find #u_content_button_1 and write that block's text on its <a>.
export function applyCopyClipboardAttrs(html, design) {
  if (!html || !design || typeof DOMParser === 'undefined') return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  let stamped = 0
  visitDesignContents(design.body && design.body.rows, (content) => {
    const links = copyLinksForContent(content)
    if (!links.length) return
    const htmlID = contentHtmlId(content)
    const root = htmlID ? doc.getElementById(htmlID) : null
    const anchors = copyAnchorsInRoot(root)
    anchors.forEach((el, index) => {
      stampCopyAttrs(el, links[index] || links[0])
      stamped += 1
    })
  })
  // If ids did not match, stamp in page order as a backup.
  if (!stamped) {
    const links = collectCopyClipboardLinks(design)
    const anchors = copyAnchorsInRoot(doc.body)
    if (links.length && anchors.length) {
      anchors.forEach((el, index) => stampCopyAttrs(el, links[index]))
    }
  }
  return doc.body ? doc.body.innerHTML : html
}

// Called after unlayer.exportHtml in Wizard / Landing Page / Shopify.
// Fixes design JSON and the HTML we save for the live store.
export function applyCopyClipboardToUnlayerExport(data) {
  if (!data || !data.design) return data
  const design = stampCopyClipboardOnDesign(data.design)
  const chunks = data.chunks || {}
  return {
    ...data,
    design,
    html: data.html ? applyCopyClipboardAttrs(data.html, design) : data.html,
    chunks: {
      ...chunks,
      body: chunks.body ? applyCopyClipboardAttrs(chunks.body, design) : chunks.body
    }
  }
}

// -----------------------------------------------------------------------------
// PART 3 — Scripts injected into the Unlayer editor (customJS)
// These run inside Unlayer's iframe. They are strings, not normal imports.
// -----------------------------------------------------------------------------

// Make "Text to copy" and "Confirmation text" use the same font as "Close popup after copy".
// Unlayer has no font setting for link-field labels.
export function getCopyLinkLabelFontJS() {
  return `(function(){
    var FONT_PROPS = ['font-family', 'font-size', 'font-weight', 'font-style', 'letter-spacing', 'line-height'];
    // Which of the three labels is this text node?
    function kind(text) {
      var t = String(text || '').replace(/^\\s+|\\s+$/g, '');
      if (t === 'Text to copy') return 'copy';
      if (t === 'Confirmation text') return 'confirm';
      if (t.indexOf('Close popup after copy') === 0) return 'close';
      return '';
    }
    // The grey label box (.input-group-text), not the raw text node.
    function labelCell(el) {
      var row = el.closest && el.closest('.input-group');
      if (!row) return el;
      var parts = row.querySelectorAll('.input-group-text, .input-group-addon, .input-group-prepend');
      for (var i = 0; i < parts.length; i++) {
        if (parts[i] === el || parts[i].contains(el)) return parts[i];
      }
      return row.firstElementChild || el;
    }
    function paint(node, styles) {
      if (!node || !node.style) return;
      for (var i = 0; i < FONT_PROPS.length; i++) {
        var val = styles[FONT_PROPS[i]];
        if (val && node.style.getPropertyValue(FONT_PROPS[i]) !== val) {
          node.style.setProperty(FONT_PROPS[i], val, 'important');
        }
      }
    }
    // Read Close popup font, then paint it onto the other two labels.
    function apply(doc) {
      if (!doc || !doc.body || !window.getComputedStyle) return;
      var walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
      var targets = [];
      var closeCell = null;
      while (walker.nextNode()) {
        var k = kind(walker.currentNode.nodeValue);
        if (!k) continue;
        var el = walker.currentNode.parentElement;
        if (!el) continue;
        var cell = labelCell(el);
        if (k === 'close') closeCell = cell;
        else targets.push({ cell: cell, el: el });
      }
      if (!closeCell || !targets.length) return;
      var cs = window.getComputedStyle(closeCell);
      var styles = {};
      for (var p = 0; p < FONT_PROPS.length; p++) styles[FONT_PROPS[p]] = cs.getPropertyValue(FONT_PROPS[p]);
      for (var i = 0; i < targets.length; i++) {
        paint(targets[i].cell, styles);
        paint(targets[i].el, styles);
        var kids = targets[i].cell.querySelectorAll('*');
        for (var k = 0; k < kids.length; k++) paint(kids[k], styles);
      }
    }
    // Unlayer rebuilds the Action panel often, and it can be in this iframe or the parent page.
    function start(doc) {
      var scheduled = 0;
      function scan() { apply(doc); }
      function schedule() {
        if (scheduled) return;
        scheduled = 1;
        requestAnimationFrame(function() { scheduled = 0; scan(); });
      }
      scan();
      if (!doc.body) return;
      new MutationObserver(schedule).observe(doc.body, { childList: true, subtree: true });
    }
    if (document.body) start(document);
    else document.addEventListener('DOMContentLoaded', function() { start(document); });
    try {
      if (window.parent && window.parent.document && window.parent.document !== document) {
        var parentDoc = window.parent.document;
        if (parentDoc.body) start(parentDoc);
        else parentDoc.addEventListener('DOMContentLoaded', function() { start(parentDoc); });
      }
    } catch (err) {}
  })();`
}

// Two jobs while the user edits a Copy action:
//   A) keep the caret in the right place (Unlayer re-renders the input on each key)
//   B) remember this block's Text to copy in __mmCopyByHtmlId (so export is per-block)
// Empty Text to copy is allowed. Save is not blocked. A live click copies nothing.
export function getCopyLinkFieldsFocusFixJS() {
  return `(function(){
    var lastKey = '';
    var lastPos = 0;
    var lastValue = '';
    var restoreUntil = 0;
    var committing = false;
    function norm(s) { return String(s || '').replace(/^\\s+|\\s+$/g, ''); }
    // Unlayer does not give these inputs a stable id. Walk up and look for "Text to copy".
    function aroundText(el) {
      var node = el;
      for (var i = 0; i < 8 && node; i++) {
        node = node.parentElement;
        if (!node) break;
        var t = node.textContent || '';
        if (t.indexOf('Text to copy') !== -1 && t.length < 500) return t;
      }
      return '';
    }
    function fieldGroup(el) {
      return el && el.closest && (el.closest('.input-group') || el.closest('.form-group') || el.closest('[class*="input-group"]'));
    }
    function fieldLabel(el) {
      var group = fieldGroup(el);
      if (!group) return '';
      var label = group.querySelector('.input-group-text, .input-group-addon, label') || group.firstElementChild;
      return norm((label && label.textContent) || '');
    }
    // "Text to copy" vs "Confirmation text" — used to put the caret back in the same field.
    function copyFieldKey(el) {
      var ph = el.placeholder || '';
      if (ph.indexOf('FREEDELIVERY') !== -1) return 'Text to copy';
      if (ph === 'Copied!') return 'Confirmation text';
      var label = fieldLabel(el);
      if (label === 'Text to copy' || label === 'Confirmation text') return label;
      return aroundText(el).indexOf('Confirmation text') !== -1 && ph === 'Copied!' ? 'Confirmation text' : 'Text to copy';
    }
    // Only our two text fields — not the URL field on a normal Open Website button.
    function isCopyField(el) {
      if (!el || !el.tagName) return false;
      var tag = el.tagName.toUpperCase();
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') return false;
      var type = (el.type || 'text').toLowerCase();
      if (tag === 'INPUT' && type !== 'text' && type !== 'search' && type !== '') return false;
      var ph = el.placeholder || '';
      if (ph.indexOf('FREEDELIVERY') !== -1) return true;
      if (ph === 'Copied!') return true;
      var label = fieldLabel(el);
      return label === 'Text to copy' || label === 'Confirmation text';
    }
    // Unlayer destroys the old input and creates a new one. Find the new one by label.
    function findCopyField(key) {
      var inputs = document.querySelectorAll('input, textarea');
      for (var i = 0; i < inputs.length; i++) {
        if (isCopyField(inputs[i]) && copyFieldKey(inputs[i]) === key) return inputs[i];
      }
      return null;
    }
    function remember(el) {
      lastKey = copyFieldKey(el);
      lastPos = typeof el.selectionStart === 'number' ? el.selectionStart : (el.value || '').length;
      lastValue = el.value || '';
    }
    // Put the typed text and caret back after Unlayer redraws the field.
    function restore() {
      if (!lastKey || Date.now() > restoreUntil) return;
      var next = findCopyField(lastKey);
      if (!next) return;
      if (next.value !== lastValue) next.value = lastValue;
      if (document.activeElement !== next) next.focus();
      try {
        var pos = Math.min(lastPos, (next.value || '').length);
        next.setSelectionRange(pos, pos);
      } catch (err) {}
    }
    // Unlayer redraws a moment later, so try a few times.
    function scheduleRestore() {
      restoreUntil = Date.now() + 400;
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(restore);
      [16, 50, 120].forEach(function(ms) { setTimeout(restore, ms); });
    }
    // Stop Unlayer seeing the key — otherwise the caret jumps after the next letter.
    function stopUnlayer(e) {
      if (!isCopyField(e.target) || committing) return;
      e.stopImmediatePropagation();
      e.stopPropagation();
    }
    // Copy fields can be in this iframe, nested iframes, or the parent page.
    function eachDoc(fn) {
      fn(document);
      var iframes = document.querySelectorAll('iframe');
      for (var i = 0; i < iframes.length; i++) {
        try { if (iframes[i].contentDocument) fn(iframes[i].contentDocument); } catch (err) {}
      }
      try {
        if (window.parent && window.parent.document && window.parent.document !== document) fn(window.parent.document);
      } catch (err) {}
    }
    // Read what is on screen now (not the interpolated HTML).
    function readCopyPanelValues() {
      var copyText = '';
      var confirmText = '';
      var closePopup = '0';
      var hasCopyField = false;
      eachDoc(function(d) {
        var inputs = d.querySelectorAll('input, textarea, select');
        for (var i = 0; i < inputs.length; i++) {
          if (!isCopyField(inputs[i])) continue;
          var key = copyFieldKey(inputs[i]);
          if (key === 'Text to copy') {
            copyText = inputs[i].value || '';
            hasCopyField = true;
          }
          if (key === 'Confirmation text') confirmText = inputs[i].value || confirmText;
        }
        var groups = d.querySelectorAll('.input-group, .form-group');
        for (var g = 0; g < groups.length; g++) {
          if ((groups[g].textContent || '').indexOf('Close popup after copy') === -1) continue;
          var native = groups[g].querySelector('select, input[type="checkbox"]');
          if (!native) continue;
          closePopup = native.type === 'checkbox' ? (native.checked ? '1' : '0') : String(native.value || '0');
        }
      });
      return { copyText: copyText, confirmText: confirmText, closePopup: closePopup, hasCopyField: hasCopyField };
    }
    // Which button/text is selected? That id is the sidecar key.
    function selectedContentHtmlId() {
      var id = '';
      eachDoc(function(d) {
        if (id) return;
        var layer = d.querySelector('.blockbuilder-layer-selected');
        if (!layer) return;
        var node = layer.querySelector('[id^="u_content_"]');
        if (node && node.id) id = node.id;
      });
      return id;
    }
    function stampAnchor(el, values) {
      if (!el || !values) return;
      el.setAttribute('data-mm-click-copy', '1');
      el.setAttribute('data-mm-copy-text', values.copyText || '');
      el.setAttribute('data-mm-confirm', values.confirmText || 'Copied!');
      el.setAttribute('data-mm-close-popup', /^(1|true|on|yes)$/i.test(String(values.closePopup)) ? '1' : '0');
    }
    // Save this block's values on window so PART 2 can read them on export.
    // Write on this window and parents because Unlayer uses nested iframes.
    function storeSidecar(htmlId, payload) {
      if (!htmlId || !payload || !payload.hasCopyField) return;
      var win = window;
      while (win) {
        try {
          win.__mmCopyByHtmlId = win.__mmCopyByHtmlId || {};
          var prev = win.__mmCopyByHtmlId[htmlId] || {};
          win.__mmCopyByHtmlId[htmlId] = {
            copyText: payload.hasCopyField ? (payload.copyText || '') : (payload.copyText || prev.copyText || ''),
            confirmText: payload.confirmText || prev.confirmText || '',
            closePopup: payload.closePopup != null ? payload.closePopup : prev.closePopup
          };
        } catch (err) {}
        if (win === win.parent) break;
        try { win = win.parent; } catch (err) { break; }
      }
    }
    // Remember values + write them onto the selected <a> in the canvas.
    function bakeSelectedCopyLink(htmlId, values) {
      htmlId = htmlId || selectedContentHtmlId();
      values = values || readCopyPanelValues();
      storeSidecar(htmlId, values);
      eachDoc(function(d) {
        var roots = [];
        var layer = d.querySelector('.blockbuilder-layer-selected');
        if (layer) roots.push(layer);
        if (htmlId) {
          var byId = d.getElementById(htmlId);
          if (byId) roots.push(byId);
        }
        var anchors = [];
        for (var r = 0; r < roots.length; r++) {
          var found = roots[r].querySelectorAll('a[data-mm-click-copy="1"]');
          for (var a = 0; a < found.length; a++) anchors.push(found[a]);
          var frames = roots[r].querySelectorAll('iframe');
          for (var f = 0; f < frames.length; f++) {
            try {
              var idoc = frames[f].contentDocument;
              if (!idoc) continue;
              var inner = idoc.querySelectorAll('a[data-mm-click-copy="1"]');
              for (var m = 0; m < inner.length; m++) anchors.push(inner[m]);
            } catch (err) {}
          }
        }
        // Prefer the word currently selected in the text editor; else the first Copy link.
        var target = [];
        for (var i = 0; i < anchors.length; i++) {
          if (anchors[i].getAttribute('data-mce-selected')) target.push(anchors[i]);
        }
        if (!target.length && anchors.length) target = [anchors[0]];
        for (var t = 0; t < target.length; t++) stampAnchor(target[t], values);
      });
    }
    // Unlayer may replace the <a> right after blur — stamp again a few times.
    function bakeSoon() {
      var htmlId = selectedContentHtmlId();
      var values = readCopyPanelValues();
      bakeSelectedCopyLink(htmlId, values);
      [50, 150, 400].forEach(function(ms) { setTimeout(function() { bakeSelectedCopyLink(htmlId, values); }, ms); });
    }

    // --- A + B: typing ---
    document.addEventListener('input', function(e) {
      stopUnlayer(e);
      if (!isCopyField(e.target) || committing) return;
      remember(e.target);
      scheduleRestore();
      bakeSelectedCopyLink();
    }, true);
    document.addEventListener('change', stopUnlayer, true);
    document.addEventListener('keyup', stopUnlayer, true);
    document.addEventListener('keypress', stopUnlayer, true);
    document.addEventListener('focusout', function(e) {
      if (!isCopyField(e.target)) return;
      var el = e.target;
      committing = true;
      restoreUntil = 0;
      try {
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        bakeSoon();
      } finally {
        committing = false;
      }
    }, true);
    document.addEventListener('focusin', function(e) {
      if (isCopyField(e.target)) remember(e.target);
    }, true);
    document.addEventListener('mousedown', function(e) {
      if (!isCopyField(e.target)) {
        lastKey = '';
        restoreUntil = 0;
      }
    }, true);
    document.addEventListener('keydown', function(e) {
      if (!isCopyField(e.target) || committing) return;
      e.stopImmediatePropagation();
      e.stopPropagation();
    }, true);
  })();`
}
